import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelectCombobox } from "./MultiSelectCombobox";

interface Column {
  key: string;
  label: string;
  filterable?: boolean;
  filterConfig?: {
    type:
      | "text"
      | "select"
      | "date-range"
      | "number"
      | "boolean"
      | "multi-select";
    options?: string[] | { label: string; value: string }[];
    placeholder?: string;
  };
}

interface AdvancedFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  columns: Column[];
  filters?: Record<string, ColumnFilter>;
  onApply: (filters: Record<string, ColumnFilter>) => void;
  onClearAll: () => void;
}

type TextFilterMode = "contains" | "equals" | "isEmpty";

interface ColumnFilter {
  value?: string | number | boolean | string[] | null;
  mode?: TextFilterMode;
  dateFrom?: Date;
  dateTo?: Date;
  numberFrom?: number;
  numberTo?: number;
}

export function AdvancedFilterPanel({
  isOpen,
  onClose,
  columns,
  filters: externalFilters,
  onApply,
  onClearAll,
}: AdvancedFilterPanelProps) {
  const [filters, setFilters] = useState<Record<string, ColumnFilter>>({});
  const panelRef = useRef<HTMLDivElement>(null);

  const mouseDownTarget = useRef<EventTarget | null>(null);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    mouseDownTarget.current = e.target;
  }, []);

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      const downTarget = mouseDownTarget.current as HTMLElement | null;
      const upTarget = e.target as HTMLElement;
      mouseDownTarget.current = null;

      if (!panelRef.current) return;

      // If the mousedown target is no longer in the DOM (e.g. a Radix
      // portal that unmounted when a dropdown closed), skip.
      if (downTarget && !document.documentElement.contains(downTarget)) return;

      // If either target is inside the panel, skip.
      if (panelRef.current.contains(upTarget)) return;
      if (downTarget && panelRef.current.contains(downTarget)) return;

      // If either target is inside a Radix portal/popper/overlay, skip.
      const isInsideOverlay = (el: HTMLElement) =>
        !!el.closest(
          "[data-radix-popper-content-wrapper], [data-radix-portal], [role='listbox'], [role='dialog'], [data-advanced-filter-toggle], [data-radix-select-content], [data-radix-select-viewport], [data-radix-dismissable-layer], [data-radix-focus-guard]",
        );
      if (isInsideOverlay(upTarget)) return;
      if (downTarget && isInsideOverlay(downTarget)) return;

      onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      // Delay listener attachment by one frame to avoid capturing stale
      // events from the click that opened the panel.
      const rafId = requestAnimationFrame(() => {
        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("mouseup", handleMouseUp);
      });
      return () => {
        cancelAnimationFrame(rafId);
        document.removeEventListener("mousedown", handleMouseDown);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isOpen, handleMouseDown, handleMouseUp]);

  useEffect(() => {
    if (externalFilters) {
      setFilters(externalFilters);
    }
  }, [externalFilters]);

  if (!isOpen) return null;

  const filterableColumns = columns.filter(
    (col) => col.filterable && col.filterConfig,
  );

  const updateFilter = (columnKey: string, update: Partial<ColumnFilter>) => {
    setFilters((prev) => ({
      ...prev,
      [columnKey]: { ...prev[columnKey], ...update },
    }));
  };

  const effectiveFilters = externalFilters ?? filters;
  const activeFilterCount = Object.keys(effectiveFilters).filter((key) => {
    const filter = effectiveFilters[key];
    if (!filter) return false;
    if (filter.mode === "isEmpty") return true;
    if (
      filter.value !== undefined &&
      filter.value !== null &&
      filter.value !== ""
    )
      return true;
    if (filter.dateFrom || filter.dateTo) return true;
    if (filter.numberFrom !== undefined || filter.numberTo !== undefined)
      return true;
    return false;
  }).length;

  const handleClearAll = () => {
    setFilters({});
    onClearAll();
  };

  const clearSingleFilter = (columnKey: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  };

  const isFilterActive = (columnKey: string): boolean => {
    const filter = filters[columnKey];
    if (!filter) return false;
    if (filter.mode === "isEmpty") return true;
    if (
      filter.value !== undefined &&
      filter.value !== null &&
      filter.value !== ""
    )
      return true;
    if (filter.dateFrom || filter.dateTo) return true;
    if (filter.numberFrom !== undefined || filter.numberTo !== undefined)
      return true;
    return false;
  };

  const renderFilterInput = (column: Column) => {
    if (!column.filterConfig) return null;

    const filter = filters[column.key] || {};
    const { type, options, placeholder } = column.filterConfig;

    switch (type) {
      case "text":
        return (
          <div className="space-y-3">
            <Select
              value={filter.mode || "contains"}
              onValueChange={(value: TextFilterMode) => {
                const update: Partial<ColumnFilter> = { mode: value };
                if (value === "isEmpty") {
                  update.value = undefined;
                }
                updateFilter(column.key, update);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contains">Contains</SelectItem>
                <SelectItem value="equals">Equals</SelectItem>
                <SelectItem value="isEmpty">Is Empty</SelectItem>
              </SelectContent>
            </Select>
            {filter.mode !== "isEmpty" && (
              <Input
                placeholder={placeholder || "Enter value..."}
                value={(filter.value as string) || ""}
                onChange={(e) =>
                  updateFilter(column.key, { value: e.target.value })
                }
              />
            )}
          </div>
        );

      case "select":
        return (
          <Select
            value={(filter.value as string) || ""}
            onValueChange={(value) => updateFilter(column.key, { value })}
          >
            <SelectTrigger>
              <SelectValue placeholder={placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {options?.map((option) => {
                const opt =
                  typeof option === "string"
                    ? { label: option, value: option }
                    : option;
                return (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        );

      case "date-range": {
        return (
          <div className="space-y-3">
            <DatePicker
              value={filter.dateFrom}
              onChange={(date) =>
                updateFilter(column.key, { dateFrom: date ?? undefined })
              }
              placeholder="From date"
              popoverClassName="z-[10060]"
            />
            <DatePicker
              value={filter.dateTo}
              onChange={(date) =>
                updateFilter(column.key, { dateTo: date ?? undefined })
              }
              placeholder="To date"
              popoverClassName="z-[10060]"
            />
          </div>
        );
      }

      case "number":
        return (
          <div className="space-y-3">
            <Input
              type="number"
              placeholder="Min value"
              value={filter.numberFrom || ""}
              onChange={(e) =>
                updateFilter(column.key, {
                  numberFrom: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            />
            <Input
              type="number"
              placeholder="Max value"
              value={filter.numberTo || ""}
              onChange={(e) =>
                updateFilter(column.key, {
                  numberTo: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />
          </div>
        );

      case "boolean":
        return (
          <Select
            value={
              filter.value === undefined ? "" : filter.value ? "true" : "false"
            }
            onValueChange={(value) =>
              updateFilter(column.key, {
                value: value === "true",
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Yes</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        );

      case "multi-select":
        return (
          <MultiSelectCombobox
            options={
              column.filterConfig?.options?.map((opt) =>
                typeof opt === "string" ? opt : opt.value,
              ) || []
            }
            selected={
              Array.isArray(filter.value) ? (filter.value as string[]) : []
            }
            onChange={(selectedValues) =>
              updateFilter(column.key, {
                value: selectedValues.length > 0 ? selectedValues : undefined,
              })
            }
            placeholder="Select values..."
          />
        );

      default:
        return null;
    }
  };

  return (
    <div
      ref={panelRef}
      className="fixed right-2 w-80 bg-background border rounded-lg shadow-lg flex flex-col overflow-hidden z-50"
      style={{ top: "8px", bottom: "8px" }}
    >
      {/* Header */}
      <div className="flex flex-col border-b flex-shrink-0">
        <div className="flex items-center justify-between px-4 py-2">
          <h2 className="text-lg font-semibold">Advanced Filters</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center justify-between px-4 pb-2">
          <Badge variant="secondary">{activeFilterCount} Active</Badge>
          <Button variant="ghost" size="sm" onClick={handleClearAll}>
            Clear All
          </Button>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
        {filterableColumns.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No filterable columns available.
          </p>
        ) : (
          filterableColumns.map((column) => (
            <div key={column.key} className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm">{column.label}</h3>
                {isFilterActive(column.key) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => clearSingleFilter(column.key)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {renderFilterInput(column)}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-t pt-4 pb-4 px-4 flex-shrink-0">
        <Button
          className="w-full"
          onClick={() => {
            onApply(filters);
            onClose();
          }}
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
}
